import React from 'react';

/**
 * Generic Stacked Bar Chart Component
 * 
 * @param {Object} data - Object containing segment values (e.g., { labor: 55.2, overhead: 24.8, profit: 20.0 })
 * @param {Array} config.segments - Array of segment configurations
 *   - key: string - matches key in data object
 *   - label: string - display label
 *   - color: string - Tailwind background color class
 *   - textColor: string - Tailwind text color class
 *   - target: number - target percentage (optional, for variance calculation)
 * @param {Array} config.targets - Array of target line configurations (optional)
 *   - value: number - percentage position (0-100)
 *   - label: string - label to display above line
 * @param {Array} config.legend - Array of legend items (optional)
 *   - color: string - Tailwind background color class
 *   - label: string - legend label
 * @param {boolean} showDetails - Whether to show detailed breakdown below chart (default: true)
 * @param {boolean} showLegend - Whether to show legend (default: true)
 * @param {boolean} showVariance - Whether to show variance from target (default: false)
 */
export const StackedBarChart = ({ 
  data, 
  config, 
  showDetails = true,
  showLegend = true,
  showVariance = false
}) => {
  const { segments, targets = [], legend = [] } = config;
  
  // Calculate total
  const total = segments.reduce((sum, segment) => sum + (data[segment.key] || 0), 0);
  
  // Calculate percentages for each segment
  const segmentData = segments.map(segment => {
    const value = data[segment.key] || 0;
    const percentage = total > 0 ? (value / total) * 100 : 0;
    const variance = segment.target ? percentage - segment.target : null;
    
    return {
      ...segment,
      value,
      percentage,
      variance
    };
  });

  return (
    <div className="w-full">
      {/* Legend */}
      {showLegend && legend.length > 0 && (
        <div className="flex flex-wrap gap-6 mb-6 text-sm">
          {legend.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className={`w-4 h-4 ${item.color} rounded`}></div>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Stacked Horizontal Bar with Target Lines */}
      <div className="relative">
        {/* Target marker lines */}
        {targets.length > 0 && (
            <div className="absolute w-full h-20" style={{ top: '-2px' }}>
                {targets.map((target, index) => (
                <div 
                    key={index}
                    className="absolute h-20 border-l-2 border-dashed border-white"
                    style={{ left: `${target.value}%` }}
                >
                <div className="absolute -top-5 -left-6 text-xs font-semibold text-gray-600">
                    {target.label}
                    </div>
                </div>
                ))}
            </div>
            )}

        {/* Stacked Bar */}
        <div className="flex h-16 rounded-lg overflow-hidden shadow-sm border border-gray-200">
          {segmentData.map((segment, index) => (
            <div 
              key={index}
              className={`${segment.color} flex items-center justify-center text-white font-semibold text-sm transition-all hover:opacity-90`}
              style={{ width: `${segment.percentage}%` }}
            >
              {segment.percentage > 8 && (
                <span>{segment.percentage.toFixed(1)}%</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Details */}
      {showDetails && (
        <div className={`mt-6 grid gap-4`} style={{ gridTemplateColumns: `repeat(${segments.length}, minmax(0, 1fr))` }}>
          {segmentData.map((segment, index) => (
            <div key={index} className="space-y-1 text-center">
              <div className="text-sm font-medium text-gray-700">{segment.label}</div>
              <div className="flex items-baseline gap-2 justify-center">
                <span className={`text-lg font-bold ${segment.textColor}`}>
                  {segment.percentage.toFixed(1)}%
                </span>
              </div>
              {segment.target && (
                <div className="text-xs text-gray-500">
                  Target: {segment.target}%
                </div>
              )}
              {segment.variance !== null && (
                <div className={`text-xs font-medium ${segment.textColor}`}>
                  Difference: {segment.variance > 0 ? '+' : ''}{segment.variance.toFixed(1)}%
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};